import { atom } from "jotai";

import { useInitializePunchPassesFromBooking$data } from "../../../hooks/__generated__/useInitializePunchPassesFromBooking.graphql";
import { punchPassesByTargetIdAtom } from "../../stores";
import {
  BookingPunchPass,
  BookingPunchPassState,
  PunchPassFragment,
} from "../../types";

const normalizePunchPassState = (
  data: useInitializePunchPassesFromBooking$data | null
): BookingPunchPassState | null => {
  if (!data) return null;

  const byTargetId: BookingPunchPassState["byTargetId"] = {};
  const bookingId = (data as { id?: string | null } | null)?.id ?? null;
  const bookingOwnerClientId = data?.ownerClient?.id ?? null;

  const mapPunchPass = (
    punchPass: PunchPassFragment | null | undefined,
    lineItemGroupId: string | null,
    orderId: string | null,
    appliesTo?: "SELF" | "OTHERS",
    valid?: boolean
  ): BookingPunchPass | null => {
    if (!punchPass?.id) return null;
    return {
      id: punchPass.id,
      lineItemGroupId,
      orderId,
      remainingPunchCount: punchPass.remainingPunchCount ?? null,
      clientId: punchPass.client?.id ?? null,
      clientName: punchPass.client?.fullName ?? null,
      name: punchPass.punchPassTemplate?.name ?? null,
      calculationType: punchPass.punchPassTemplate?.calculationType ?? null,
      type: punchPass.punchPassTemplate?.type ?? null,
      appliesTo,
      valid,
    };
  };

  data?.orders?.edges?.forEach((orderEdge) => {
    const lineItemGroups = orderEdge?.node?.lineItemGroups ?? [];
    const orderId = orderEdge?.node?.id ?? null;
    lineItemGroups?.forEach((lineItemGroup) => {
      const isBookingLevel = lineItemGroup?.type === "BOOKING";
      const targetId = isBookingLevel
        ? bookingId
        : lineItemGroup?.round?.id ?? null;

      // Round player client id (for PLAYER-type lineItemGroups)
      const roundPlayerClientId = !isBookingLevel
        ? lineItemGroup?.round?.player?.id ?? null
        : null;

      if (!targetId) return;

      const current = byTargetId[targetId] ?? {
        appliedRatePunchPass: null,
        appliedTransportationPunchPass: null,
        availableRatePunchPasses: [],
        availableTransportationPunchPasses: [],
        invalidPunchPasses: [],
      };

      const appliedPunchPass = mapPunchPass(
        lineItemGroup?.punchPass,
        lineItemGroup?.id ?? null,
        orderId
      );

      if (appliedPunchPass) {
        if (appliedPunchPass.type === "TRANSPORTATION") {
          current.appliedTransportationPunchPass =
            current.appliedTransportationPunchPass ?? appliedPunchPass;
        } else {
          current.appliedRatePunchPass =
            current.appliedRatePunchPass ?? appliedPunchPass;
        }
      }

      const optionSets: Array<{
        list:
          | ReadonlyArray<PunchPassFragment | null | undefined>
          | null
          | undefined;
        appliesTo: "SELF" | "OTHERS";
        valid: boolean;
      }> = [
        {
          list: lineItemGroup?.punchPassesSelfValid,
          appliesTo: "SELF",
          valid: true,
        },
        {
          list: lineItemGroup?.punchPassesOthersValid,
          appliesTo: "OTHERS",
          valid: true,
        },
        {
          list: lineItemGroup?.punchPassesSelfInvalid,
          appliesTo: "SELF",
          valid: false,
        },
      ];

      optionSets.forEach(({ list, appliesTo, valid }) => {
        list?.forEach((option) => {
          const normalized = mapPunchPass(
            option,
            lineItemGroup?.id ?? null,
            orderId,
            appliesTo,
            valid
          );

          if (!normalized) return;

          if (!valid) {
            current.invalidPunchPasses.push(normalized);
            return;
          }

          // Filter available punch passes based on booking owner vs round player
          const clientId = normalized.clientId;
          let isAvailable = true;

          if (clientId) {
            if (isBookingLevel) {
              // For booking-level, only passes belonging to the booking owner are available
              if (bookingOwnerClientId && clientId !== bookingOwnerClientId) {
                isAvailable = false;
              }
            } else {
              // For round-level, availability depends on whether the round player is the owner
              const ownerId = bookingOwnerClientId;
              const playerId = roundPlayerClientId;

              if (ownerId && playerId) {
                if (ownerId === playerId) {
                  // Player is the booking owner: only owner's passes are available
                  isAvailable = clientId === ownerId;
                } else {
                  // Player is not the owner: owner can use their own passes and the player's passes
                  isAvailable = clientId === ownerId || clientId === playerId;
                }
              } else if (ownerId) {
                isAvailable = clientId === ownerId;
              } else if (playerId) {
                isAvailable = clientId === playerId;
              }
            }
          }

          if (!isAvailable) {
            return;
          }

          if (normalized.type === "TRANSPORTATION") {
            current.availableTransportationPunchPasses.push(normalized);
          } else {
            current.availableRatePunchPasses.push(normalized);
          }
        });
      });

      byTargetId[targetId] = current;
    });
  });

  return { byTargetId };
};

/**
 * Action atom to initialize punch pass state from booking data.
 * Re-initializes the state each time it's called with new data.
 */
export const initializePunchPassesAtom = atom(
  null,
  (
    get,
    set,
    payload: { data: useInitializePunchPassesFromBooking$data | null }
  ) => {
    const normalized = normalizePunchPassState(payload.data);
    if (!normalized) return;

    set(punchPassesByTargetIdAtom, normalized);
  }
);
